/* eslint-disable boundaries/element-types */
import { Table } from '@radix-ui/themes'
import { useNavigate } from '@tanstack/react-router'
import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  FilterFn,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowData,
  RowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Skeleton } from 'antd'
import { normalizeString } from 'components/table/enhancements/filters/filterUtils'
import { Pagination } from 'components/table/enhancements/Pagination'
import { TableTopBar } from 'components/table/enhancements/TableTopBar'
import { compareAsc, parseISO } from 'date-fns'
import { isEqual } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { css, styled } from 'styled-components'
import { InputType } from 'types'

import { HeaderCell } from './cells/HeaderCell'
import { TableBodyRow } from './TableBodyRow'

const StyledTableRoot = styled(Table.Root)`
  height: 100%;

  table {
    border-collapse: separate;
    border-spacing: 0 2px;
  }

  tr td:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  tr td:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`

const TableContainer = styled.div`
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  overflow: auto;
  flex-grow: 1;
  padding: 0 0.5rem;
`
const TopBarContainer = styled.div`
  margin-bottom: 12px;
`
const StyledSubheaderSection = styled.div`
  padding-bottom: 0.5rem;
`

interface StyledTableHeaderProps {
  $isTableHeaderSticky?: boolean
}

const StyledTableHeader = styled(Table.Header)<StyledTableHeaderProps>`
  color: ${({ theme }) => theme.forecast.defaultText};
  height: 2.5rem;
  flex-grow: 0;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.forecast.pageBackground1};

  ${({ $isTableHeaderSticky }) =>
    $isTableHeaderSticky &&
    `
    position: sticky;
    top: 0;
    z-index: 2;
  `}
`

const StyledTableCell = styled(Table.Cell)`
  color: ${({ theme }) => theme.forecast.defaultText};
  padding: 8px;
  text-align: left;
  vertical-align: middle;
  min-height: 56px;
  border-style: hidden !important;
  white-space: nowrap;
`

const StyledTableBody = styled(Table.Body)`
  background-color: ${({ theme }) => theme.forecast.navSidebarBackground};
`

const StyledTableRow = styled(Table.Row)<{ $hasOnClick: boolean }>`
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  position: relative;

  ${({ $hasOnClick }) =>
    $hasOnClick &&
    css`
      &:hover {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }

        &:hover * {
          cursor: pointer;
        }
      }

      &:active {
        ${StyledTableCell} {
          background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
        }
      }
    `}
`

const StyledPaginationContainer = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  display: flex;
  flex-direction: row;
  height: 56px;
  justify-content: space-between;
  align-items: center;
  bottom: 0;
  width: 100%;
  padding: 0 0.25rem;
  flex-grow: 0;
  flex-shrink: 0;
`

declare module '@tanstack/react-table' {
  // Typescript is complaining that TData and TValue are unused, but they are necessary in the ColumnMeta interface
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    inputType: InputType
  }

  interface FilterFns {
    customGlobalFilter: FilterFn<unknown>
    dateRangeFilter: FilterFn<unknown>
    multiSelectFilter: FilterFn<unknown>
  }
}

type ColumnDefWithAccessor<TData> = ColumnDef<TData> & { accessorKey?: string }

type ExpandableProps<TData extends object> =
  | {
      enableExpanding: true
      getRowCanExpand: (row: Row<TData>) => boolean
      getSubRows: (row: TData) => TData[] | undefined
      filterFromLeafRows: boolean
    }
  | {
      enableExpanding?: false
      getRowCanExpand?: never
      getSubRows?: never
      filterFromLeafRows?: never
    }

export type TableComponentProps<TData extends object> = {
  data: TData[]
  isLoadingData: boolean
  columns: ColumnDef<TData>[]
  footerName: string
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => void
  showFilterBar?: boolean
  globalFilterFn?: FilterFn<TData>
  leadingContent?: React.ReactNode
  subHeaderContent?: React.ReactNode
  onFilteredDataChange?: (
    filteredData: RowModel<TData>,
    globalFilter: unknown,
    columnFilters: ColumnFiltersState
  ) => void
  showTable?: boolean
  showColumnConfig?: boolean
  initialColumnFilters?: ColumnFiltersState
  urlFilters?: Record<
    string,
    string | number | boolean | string[] | [string | null, string | null]
  >
  disableUrlFilters?: boolean
  defaultVisibleColumnIds?: string[]
  isTableHeaderSticky?: boolean
} & ExpandableProps<TData>

export const TableComponent = <TData extends object>(
  props: TableComponentProps<TData>
) => {
  const navigate = useNavigate()

  const {
    isLoadingData,
    data,
    columns,
    footerName,
    onRowClick,
    pagination,
    setPagination,
    setSorting,
    sorting,
    showFilterBar = true,
    globalFilterFn,
    leadingContent,
    onFilteredDataChange,
    showTable = true,
    showColumnConfig = true,
    initialColumnFilters = [],
    urlFilters = {},
    disableUrlFilters = false,
    defaultVisibleColumnIds,
    isTableHeaderSticky = true,
    // Expandable props
    enableExpanding = false,
    getRowCanExpand,
    getSubRows,
    filterFromLeafRows,
  } = props

  const [globalFilter, setGlobalFilter] = useState<unknown>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean
  }>({})
  const [expandedState, setExpandedState] = useState<ExpandedState>({})
  const hasInitialized = React.useRef(false)
  const previousUrlFilters = React.useRef(urlFilters)
  const prevFiltersRef = React.useRef<ColumnFiltersState>(columnFilters)
  const prevGlobalFilterRef = React.useRef(globalFilter)

  // Convert URL filters to column filters state
  const initialFilters = React.useMemo(() => {
    return Object.entries(urlFilters).map(([id, value]) => ({
      id,
      value,
    }))
  }, [urlFilters])

  const defaultFilterFn: FilterFn<TData> = () => true

  const dateRangeFilterFn: FilterFn<TData> = (row, columnId, filterValue) => {
    const [startDateStr, endDateStr] = filterValue
    const rowValue = row.getValue(columnId)

    // Return true if no filter is applied
    if (!rowValue || (!startDateStr && !endDateStr)) return true

    try {
      // Handle both Date objects and string values
      let rowDate: Date
      if (rowValue instanceof Date) {
        rowDate = rowValue
      } else {
        const rowDateStr = String(rowValue)
        rowDate = parseISO(rowDateStr)
        if (isNaN(rowDate.getTime())) return true
      }

      // Parse filter dates only if they exist
      const startDate = startDateStr ? parseISO(startDateStr) : null
      const endDate = endDateStr ? parseISO(endDateStr) : null

      // Validate parsed dates
      if (startDate && isNaN(startDate.getTime())) return true
      if (endDate && isNaN(endDate.getTime())) return true

      // Set end date to end of day for inclusive comparison
      if (endDate) {
        endDate.setHours(23, 59, 59, 999)
      }

      const isAfterStart = startDate
        ? compareAsc(rowDate, startDate) >= 0
        : true
      const isBeforeEnd = endDate ? compareAsc(rowDate, endDate) <= 0 : true

      return isAfterStart && isBeforeEnd
    } catch (error) {
      console.error('Error in dateRangeFilterFn:', error)
      return true
    }
  }

  const multiSelectFilterFn: FilterFn<TData> = (row, columnId, filterValue) => {
    const rowValue = row.getValue(columnId)
    const normalizedFilterValues = filterValue.map((v: string) =>
      normalizeString(v)
    )

    const processValue = (value: string) => {
      const normalizedValue = normalizeString(value)
      return normalizedFilterValues.includes(normalizedValue)
    }

    if (typeof rowValue === 'string') {
      return processValue(rowValue)
    } else if (Array.isArray(rowValue)) {
      return rowValue.some(
        (item) => typeof item === 'string' && processValue(item)
      )
    }

    return false
  }

  const tableConfig = useMemo(
    () => ({
      data,
      autoResetPageIndex: false,
      columns,
      filterFns: {
        customGlobalFilter: globalFilterFn ?? defaultFilterFn,
        dateRangeFilter: dateRangeFilterFn,
        multiSelectFilter: multiSelectFilterFn,
      },
      getCoreRowModel: getCoreRowModel<TData>(),
      getFacetedRowModel: getFacetedRowModel<TData>(),
      getFilteredRowModel: getFilteredRowModel<TData>(),
      getSortedRowModel: getSortedRowModel<TData>(),
      onSortingChange: setSorting,
      getPaginationRowModel: getPaginationRowModel<TData>(),
      // Expandable configuration
      ...(enableExpanding && {
        getExpandedRowModel: getExpandedRowModel<TData>(),
        getSubRows,
        getRowCanExpand,
        filterFromLeafRows,
        onExpandedChange: setExpandedState,
      }),
      state: {
        globalFilter,
        columnFilters,
        pagination,
        sorting,
        columnVisibility,
        ...(enableExpanding && { expanded: expandedState }),
      },
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: globalFilterFn,
      onPaginationChange: setPagination,
      onColumnFiltersChange: (
        updaterOrValue:
          | ColumnFiltersState
          | ((old: ColumnFiltersState) => ColumnFiltersState)
      ) => {
        const newFilters =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(columnFilters)
            : updaterOrValue
        setColumnFilters(newFilters)
        // Do not clear global filter when column filters are cleared
      },
      onColumnVisibilityChange: setColumnVisibility,
      getFacetedUniqueValues: getFacetedUniqueValues<TData>(),
      getFacetedMinMaxValues: getFacetedMinMaxValues<TData>(),
    }),
    [
      data,
      columns,
      globalFilterFn,
      globalFilter,
      columnFilters,
      pagination,
      sorting,
      columnVisibility,
      setSorting,
      setPagination,
      setColumnVisibility,
      enableExpanding,
      getSubRows,
      getRowCanExpand,
      expandedState,
      setExpandedState,
      filterFromLeafRows,
    ]
  )

  const table = useReactTable<TData>(tableConfig)

  // Initialize filters and column visibility only on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      if (defaultVisibleColumnIds && columns.length > 0) {
        const visibility: { [key: string]: boolean } = {}
        columns.forEach((col) => {
          const id = col.id
          const accessorKey = (col as ColumnDefWithAccessor<TData>).accessorKey
          const key = id || accessorKey
          if (typeof key === 'string') {
            visibility[key] = defaultVisibleColumnIds.includes(key)
          }
        })
        setColumnVisibility(visibility)
      }
      const filtersToApply =
        initialFilters.length > 0 ? initialFilters : initialColumnFilters || []
      if (filtersToApply.length > 0) {
        setColumnFilters(filtersToApply)
      }
      hasInitialized.current = true
    }
    // we need to do this to ensure that the filters are applied when the table is mounted
    // and not when the table is re-rendered
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes and notify parent
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange(
        table.getFilteredRowModel(),
        globalFilter,
        columnFilters
      )
    }
  }, [onFilteredDataChange, table, globalFilter, columnFilters])

  // Reset filters when URL filters are cleared
  useEffect(() => {
    // Only clear filters if URL filters were cleared by user action (not initially empty)
    const previousHadFilters =
      Object.keys(previousUrlFilters.current).length > 0
    const currentHasNoFilters = Object.keys(urlFilters).length === 0

    if (
      hasInitialized.current &&
      previousHadFilters &&
      currentHasNoFilters &&
      columnFilters.length > 0
    ) {
      setColumnFilters([])
      setGlobalFilter([])
    }

    // Update the previous URL filters reference
    previousUrlFilters.current = urlFilters

    // this is needed to ensure that the filters are reset when the url filters are cleared
    // and not when the table is re-rendered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters])

  // Reset to first page when filters change
  useEffect(() => {
    const filtersChanged = !isEqual(prevFiltersRef.current, columnFilters)
    const globalFilterChanged = !isEqual(
      prevGlobalFilterRef.current,
      globalFilter
    )
    if (filtersChanged || globalFilterChanged) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      prevFiltersRef.current = columnFilters
      prevGlobalFilterRef.current = globalFilter
    }
  }, [columnFilters, globalFilter, setPagination])

  return (
    <>
      {(showFilterBar || globalFilterFn || leadingContent) && (
        <TopBarContainer>
          <TableTopBar
            columns={columns}
            setGlobalFilter={setGlobalFilter}
            getColumn={table.getColumn}
            showFilterBar={showFilterBar}
            showGlobalFilter={!!globalFilterFn}
            showColumnConfig={showColumnConfig}
            leadingContent={leadingContent}
            initialColumnFilters={initialColumnFilters}
            navigate={navigate}
            disableUrlFilters={disableUrlFilters}
            defaultVisibleColumnIds={defaultVisibleColumnIds}
          />
        </TopBarContainer>
      )}

      {showTable && (
        <>
          {props.subHeaderContent && (
            <StyledSubheaderSection>
              {props.subHeaderContent}
            </StyledSubheaderSection>
          )}

          <TableContainer>
            {isLoadingData ? (
              <Skeleton />
            ) : (
              <>
                <StyledTableRoot>
                  <StyledTableHeader $isTableHeaderSticky={isTableHeaderSticky}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Table.Row key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return header.isPlaceholder ? (
                            <th key={header.id} colSpan={header.colSpan}></th>
                          ) : (
                            <HeaderCell
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              canSort={header.column.getCanSort()}
                              isSorted={header.column.getIsSorted()}
                              columnId={header.id}
                              cellText={
                                header.column.columnDef.header?.toString() || ''
                              }
                            />
                          )
                        })}
                      </Table.Row>
                    ))}
                  </StyledTableHeader>
                  <StyledTableBody>
                    {table.getRowModel().rows?.length ? (
                      table
                        .getRowModel()
                        .rows.map((row) => (
                          <TableBodyRow
                            key={row.id}
                            row={row}
                            enableExpanding={enableExpanding}
                            onRowClick={onRowClick}
                          />
                        ))
                    ) : (
                      <StyledTableRow $hasOnClick={Boolean(onRowClick)}>
                        <StyledTableCell colSpan={columns.length}>
                          No results.
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </StyledTableBody>
                </StyledTableRoot>
              </>
            )}
          </TableContainer>
          <StyledPaginationContainer>
            <div>{`${table.getFilteredRowModel().rows.length} ${footerName}`}</div>
            {table.getPageCount() > 1 && (
              <Pagination
                getCanNextPage={table.getCanNextPage}
                getCanPreviousPage={table.getCanPreviousPage}
                tableNextPage={table.nextPage}
                tablePreviousPage={table.previousPage}
                tablePagesCount={table.getPageCount()}
                tableGetToPage={table.setPageIndex}
                tableCurrentPage={pagination.pageIndex}
              />
            )}
          </StyledPaginationContainer>
        </>
      )}
    </>
  )
}
